import { fg } from '@atlassian/jira-feature-gating';
import type { ModalLoadQueryData } from '../../common/types/index.tsx';
import useFetchCreateConfigQueryInContext from '../fetch-create-config-query-in-context/index.tsx';
import useFetchCreateConfigQuery from '../fetch-create-config-query/index.tsx';
import useFetchSubtaskCreateConfigQuery from '../fetch-subtask-create-config-query/index.tsx';
import type { Props } from './types.tsx';
import { usePrefillCustomFields } from './use-prefilled-custom-fields/index.tsx';

const useModalLoadQuery = (props: Props): ModalLoadQueryData => {
	const {
		displayOnlyRequiredFields = false,
		triggerPointKey,
		isSubTaskCreationOpen = false,
		triggerIssueCreatePayload,
		isModalLoadInContext = false,
		isCreateLinkedIssue,
	} = props;
	const [
		{
			isLoading,
			fieldConfig: fieldConfigInitial,
			fieldTabs,
			contextFields,
			meta,
			error,
			apiLatency,
			additionalServices,
			storageLimitsData,
		},
		fetchConfig,
	] = useFetchCreateConfigQuery({ displayOnlyRequiredFields, triggerPointKey });

	const fieldConfig = usePrefillCustomFields(fieldConfigInitial);

	const [
		{
			isLoading: isSubtaskLoading,
			fieldConfig: subtaskFieldConfig,
			fieldTabs: subtaskFieldTabs,
			contextFields: subtaskContextFields,
			meta: subtaskFieldsDataMeta,
			error: subtaskFieldsDataError,
			apiLatency: subtaskApiLatency,
			additionalServices: subtaskAdditionalServices,
			storageLimitsData: subtaskStorageLimitsData,
		},
		fetchSubtaskConfig,
	] = useFetchSubtaskCreateConfigQuery({ displayOnlyRequiredFields, triggerPointKey });

	const [
		{
			isLoading: isLoadingInContext,
			fieldConfig: fieldConfigInContext,
			fieldTabs: fieldTabsInContext,
			contextFields: contextFieldsInContext,
			meta: fieldsDataMetaInContext,
			error: fieldsDataErrorInContext,
			apiLatency: apiLatencyInContext,
			additionalServices: additionalServicesInContext,
			storageLimitsData: inContextStorageLimitsData,
		},
		fetchConfigInContext,
	] = useFetchCreateConfigQueryInContext({
		displayOnlyRequiredFields,
		triggerPointKey,
		...(fg('create_linked_issues_trigger_point_issue_view') ? { isCreateLinkedIssue } : {}),
	});

	const parentId = triggerIssueCreatePayload?.parentId || null;
	const additionalContext = triggerIssueCreatePayload?.additionalContext || null;
	if (isModalLoadInContext && additionalContext) {
		return [
			{
				isLoading: isLoadingInContext,
				fieldConfig: fieldConfigInContext,
				fieldTabs: fieldTabsInContext,
				contextFields: contextFieldsInContext,
				meta: fieldsDataMetaInContext,
				error: fieldsDataErrorInContext,
				apiLatency: apiLatencyInContext,
				additionalServices: additionalServicesInContext,
				storageLimitsData: inContextStorageLimitsData,
			},
			fetchConfigInContext,
		];
	}
	if (isSubTaskCreationOpen && parentId) {
		return [
			{
				isLoading: isSubtaskLoading,
				fieldConfig: subtaskFieldConfig,
				fieldTabs: subtaskFieldTabs,
				contextFields: subtaskContextFields,
				meta: subtaskFieldsDataMeta,
				error: subtaskFieldsDataError,
				apiLatency: subtaskApiLatency,
				additionalServices: subtaskAdditionalServices,
				storageLimitsData: subtaskStorageLimitsData,
			},
			fetchSubtaskConfig,
		];
	}
	return [
		{
			isLoading,
			fieldConfig,
			fieldTabs,
			contextFields,
			meta,
			error,
			apiLatency,
			additionalServices,
			storageLimitsData,
		},
		fetchConfig,
	];
};

export default useModalLoadQuery;
